import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import aulaService from "../../../../services/v2/aula.service";
import {
	AjustesPortal, AlumnoLista, apiNotasPersonal, apiPersonal, apiTareasPersonal, ClaseDelPersonal, listaDelPersonal, Materiales, PortalApiPersonal, PublicacionPortal,
	ResumenClase, UnidadPortal
} from "../../../../services/v2/classPortal.service";
import { abrirAulaEnPestana, mensajeDeAula } from "../../../../utils/abrirAula";
import {
	Aviso, Avatar, exito, fondoDePortada, Icono, LineaAviso, mensajeDeError, PatronSvg, problema, Redactor, reducirImagen, subirArchivo, TarjetaPublicacion, useAlVolver
} from "../../portalClase/comun";
import { apiContenido } from "../../../../services/v2/lmsContenido.service";
import PanelContenido from "../../portalClase/contenido/panelContenido";
import PanelEvaluacion from "../../portalClase/contenido/panelEvaluacion";
import TareasPanel from "../../portalClase/tareas/tareasPanel";
import NuevaTarea from "../../portalClase/tareas/nuevaTarea";
import Calificar from "../../portalClase/tareas/calificar";
import LibroDeNotasPanel from "../../portalClase/notas/libroDeNotas";
import { esBaja, notaTxt } from "../../portalClase/notas/comunNotas";
import ResultadosQuizPantalla from "../../portalClase/quiz/resultadosQuiz";
import { RedactorEncuesta } from "../../portalClase/quiz/encuesta";
import { apiQuizPersonal } from "../../../../services/v2/lmsQuiz.service";
import "../../portalClase/portalClase.css";

/*
	Mis Clases (sección classPortal, id 97) — Portal de Clase, lado del personal (Parte A, 2026-10-17).
	Lista de sus horarios vigentes → portal de la clase con Muro, Materiales y Alumnos, y Personalizar. Una sola ruta:
	/classPortal?h=<horario> abre una clase y &v=personalizar la pantalla de ajustes. Carga al abrir y al volver a la pestaña
	(visibilitychange); nada de intervalos. Tareas y Calificar llegaron en la Parte B; el Libro De Notas (pestaña Notas) en la C;
	quiz (Resultados, &v=resultados) y encuestas del muro en la D.
*/

type Pestana = "muro" | "tareas" | "materiales" | "contenido" | "alumnos" | "notas"
const PESTANAS: { id: Pestana; texto: string }[] = [{ id: "muro", texto: "Muro" }, { id: "tareas", texto: "Tareas" }, { id: "materiales", texto: "Materiales" }, { id: "contenido", texto: "Contenido" }, { id: "alumnos", texto: "Alumnos" }, { id: "notas", texto: "Notas" }]

// ---------------------------------------------------------------- lista

const ListaDeClases: React.FC<{ onAbrir: (h: number) => void }> = ({ onAbrir }) => {
	const [todas, setTodas] = useState<boolean>(false)
	const [q, setQ] = useState<string>("")
	const [datos, setDatos] = useState<{ canSeeAll: boolean; hasMore: boolean; classes: ClaseDelPersonal[] } | null>(null)
	const [error, setError] = useState<string | null>(null)
	const pedido = useRef<number>(0)
	const auto = useRef<boolean>(false)

	const cargar = useCallback(() => {
		const n = ++pedido.current
		listaDelPersonal(todas, q.trim()).then((r) => {
			if (n !== pedido.current) return
			//Whoever doesn't teach (Super Admin, Servicios Académicos) lands on "Todas" instead of an empty list (QA B15).
			if (!todas && !q.trim() && r.canSeeAll && r.classes.length === 0 && !auto.current) { auto.current = true; setTodas(true); return }
			setDatos(r); setError(null)
		})
			.catch((e) => { if (n === pedido.current) setError(mensajeDeError(e, "No pudimos cargar tus clases.")) })
	}, [todas, q])

	//Con búsqueda se espera medio segundo a que termine de escribir.
	useEffect(() => { const t = window.setTimeout(cargar, q ? 400 : 0); return () => window.clearTimeout(t) }, [cargar, q])
	useAlVolver(cargar)

	return (
		<div className="pc">
			<div className="pcPagina">
				<h1 className="pcT" style={{ margin: 0, fontSize: 34, fontWeight: 600 }}>Mis Clases</h1>
				<div className="pcBuscador">
					<label htmlFor="mcBuscar" style={{ position: "absolute", left: -9999 }}>Buscar clase</label>
					<input id="mcBuscar" className="pcInput" placeholder="Buscar Por Curso, Horario O Maestro" value={q} onChange={(e) => setQ(e.target.value)} />
					{datos?.canSeeAll ? (
						<button type="button" className={"pcBtn " + (todas ? "pcBtnP" : "pcBtnS")} aria-pressed={todas} onClick={() => setTodas(!todas)}>Ver Todas De Mis Planteles</button>
					) : null}
					<button type="button" className="pcBtn pcBtnS" onClick={cargar}>Actualizar</button>
				</div>
				{error ? <div className="pcAviso pcOro" role="alert">{error}</div> : null}
				{datos === null && !error ? <div className="pcCargando">Cargando…</div> : null}
				{datos && datos.classes.length === 0 ? <div className="pcVacio">{todas || q ? "Sin Resultados" : "No Tienes Clases Vigentes"}</div> : null}
				<div className="pcClases">
					{datos?.classes.map((c) => (
						<button key={c.scheduleId} type="button" className="pcClaseTarjeta" onClick={() => onAbrir(c.scheduleId)}>
							<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
								<span className="pcChip pcChipAcento">{c.modality === "virtual" ? "Virtual" : "Presencial"}</span>
							</div>
							<div>
								<div className="pcClaseTitulo">{c.title}</div>
								{c.period ? <div className="pcClasePeriodo">{c.period}</div> : null}
							</div>
							<div className="pcTenue">{[c.scheduleLabel, c.mentor || "Sin Maestro"].filter(Boolean).join(" · ")}</div>
							<div className="pcTenue pcChico">{[c.campus, c.students + (c.students === 1 ? " Alumno" : " Alumnos")].filter(Boolean).join(" · ")}</div>
						</button>
					))}
				</div>
				{datos?.hasMore ? <div className="pcVacio">Hay más clases: usa el buscador para encontrarlas.</div> : null}
			</div>
		</div>
	)
}

// ---------------------------------------------------------------- unidades

const FormUnidad: React.FC<{ inicial?: UnidadPortal; onGuardar: (d: { name: string; startsOn: string | null; endsOn: string | null }) => Promise<void>; onCancelar: () => void }> = ({ inicial, onGuardar, onCancelar }) => {
	const [nombre, setNombre] = useState<string>(inicial?.name || "")
	const [desde, setDesde] = useState<string>(inicial?.startsOn || "")
	const [hasta, setHasta] = useState<string>(inicial?.endsOn || "")
	const [error, setError] = useState<string | null>(null)
	const [ocupado, setOcupado] = useState<boolean>(false)
	const guardar = async () => {
		if (nombre.trim() === "") { setError("Ponle nombre a la unidad."); return }
		setOcupado(true); setError(null)
		try { await onGuardar({ name: nombre.trim(), startsOn: desde || null, endsOn: hasta || null }) } catch (e) { setError(mensajeDeError(e)) } finally { setOcupado(false) }
	}
	return (
		<div className="pcTarjeta">
			<div className="pcCampo"><label className="pcCampoEtiqueta" htmlFor="fuNombre">Nombre De La Unidad</label>
				<input id="fuNombre" className="pcInput" value={nombre} maxLength={120} onChange={(e) => setNombre(e.target.value)} /></div>
			<div className="pcRedactorBotones">
				<div className="pcCampo" style={{ flex: "1 1 160px" }}><label className="pcCampoEtiqueta" htmlFor="fuDesde">Desde</label>
					<input id="fuDesde" type="date" className="pcInput" value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
				<div className="pcCampo" style={{ flex: "1 1 160px" }}><label className="pcCampoEtiqueta" htmlFor="fuHasta">Hasta</label>
					<input id="fuHasta" type="date" className="pcInput" value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
			</div>
			<div className="pcRedactorPie"><div className="pcDerecha">
				<button type="button" className="pcBtn pcBtnS" onClick={onCancelar}>Cancelar</button>
				<button type="button" className="pcBtn pcBtnP" disabled={ocupado} onClick={guardar}>Guardar</button>
			</div></div>
			{error ? <div className="pcAviso pcOro" role="alert">{error}</div> : null}
		</div>
	)
}

// ---------------------------------------------------------------- portal de una clase

const PortalDeClase: React.FC<{ h: number; onVolver: () => void; onPersonalizar: () => void; refrescarAjustes: number; pestanaInicial?: Pestana; avisoInicial?: string | null;
	onNuevaTarea: () => void; onEditarTarea: (t: number) => void; onCalificar: (t: number, desde?: "notas" | "muro") => void; onResultados: (t: number, desde?: "notas" | "muro") => void }> = ({ h, onVolver, onPersonalizar, refrescarAjustes, pestanaInicial, avisoInicial, onNuevaTarea, onEditarTarea, onCalificar, onResultados }) => {
	const api: PortalApiPersonal = React.useMemo(() => apiPersonal(h), [h])
	const navigate = useNavigate()
	const [res, setRes] = useState<ResumenClase | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [pestana, setPestana] = useState<Pestana>(pestanaInicial || "muro")
	const [versionTareas, setVersionTareas] = useState<number>(0)
	const apiT = React.useMemo(() => apiTareasPersonal(h), [h])
	const apiN = React.useMemo(() => apiNotasPersonal(h), [h])
	const [posts, setPosts] = useState<PublicacionPortal[] | null>(null)
	const [hayMas, setHayMas] = useState<boolean>(false)
	const [pagina, setPagina] = useState<number>(1)
	const [mat, setMat] = useState<Materiales | null>(null)
	const [alumnos, setAlumnos] = useState<{ minGrade: number; students: AlumnoLista[] } | null>(null)
	const [redactor, setRedactor] = useState<{ modo: "anuncio" | "material" | "pendiente" | "encuesta"; editando: PublicacionPortal | null } | null>(null)
	const apiCont = React.useMemo(() => apiContenido("horario", h), [h])
	const [unidadForm, setUnidadForm] = useState<{ editar: UnidadPortal | null } | null>(null)
	const [aviso, setAviso] = useState<Aviso | null>(avisoInicial ? exito(avisoInicial) : null)
	//The composer shows who is writing (a co-teacher is not the main teacher) (QA B2).
	const yo = useSelector((s: any) => {
		const u = s.rootReducer?.sessionState?.user
		const n = String(u?.first_name || "").trim().split(/\s+/)[0] || ""
		const a = String(u?.last_name || "").trim().split(/\s+/)[0] || ""
		return { nombre: (n + " " + a).trim(), iniciales: ((n[0] || "") + (a[0] || "")).toUpperCase() }
	}, (x, y) => x.nombre === y.nombre)
	const [abriendoAula, setAbriendoAula] = useState<boolean>(false)

	const cargarResumen = useCallback(() => {
		api.overview().then((r) => { setRes(r); setError(null) }).catch((e) => setError(mensajeDeError(e, "No pudimos abrir esta clase.")))
	}, [api])
	const cargarMuro = useCallback(() => {
		api.wall(1).then((m) => { setPosts(m.posts); setHayMas(m.hasMore); setPagina(1) }).catch(() => setPosts((p) => p || []))
	}, [api])
	const cargarMateriales = useCallback(() => { api.materials().then(setMat).catch(() => setMat((m) => m || { visible: true, units: [], posts: [] })) }, [api])
	const cargarTodo = useCallback(() => { cargarResumen(); cargarMuro(); if (pestana === "materiales") cargarMateriales() }, [cargarResumen, cargarMuro, cargarMateriales, pestana])

	useEffect(() => { cargarResumen(); cargarMuro() }, [cargarResumen, cargarMuro, refrescarAjustes])
	useEffect(() => { if (pestana === "materiales" && mat === null) cargarMateriales() }, [pestana, mat, cargarMateriales])
	// Alumnos se vuelve a pedir CADA vez que se abre la pestaña (los promedios cambian al calificar en otra pestaña o pantalla).
	const cargarAlumnos = useCallback(() => { api.roster().then(setAlumnos).catch(() => setAlumnos((x) => x || { minGrade: 70, students: [] })) }, [api])
	useEffect(() => { if (pestana === "alumnos") cargarAlumnos() }, [pestana, cargarAlumnos])
	useAlVolver(useCallback(() => { cargarTodo(); if (pestana === "alumnos") cargarAlumnos() }, [cargarTodo, cargarAlumnos, pestana]))

	if (error) return <div className="pc"><div className="pcPagina"><div className="pcMigas"><button type="button" onClick={onVolver}>Mis Clases</button></div><div className="pcAviso pcOro" role="alert">{error}</div></div></div>
	if (res === null) return <div className="pc"><div className="pcCargando">Cargando…</div></div>

	const a: AjustesPortal = res.settings
	const unidades: UnidadPortal[] = mat?.units || []
	const reemplazar = (lista: PublicacionPortal[] | null, p: PublicacionPortal) => (lista || []).map((x) => (x.id === p.id ? p : x))
	const quitar = (id: number) => { setPosts((l) => (l || []).filter((x) => x.id !== id)); setMat((m) => (m ? { ...m, posts: m.posts.filter((x) => x.id !== id) } : m)) }
	const cambiar = (p: PublicacionPortal) => { setPosts((l) => reemplazar(l, p)); setMat((m) => (m ? { ...m, posts: reemplazar(m.posts, p) } : m)) }
	const alGuardarPost = (p: PublicacionPortal) => {
		const eraNuevo = !redactor?.editando
		setRedactor(null)
		cargarMuro()
		if (mat !== null || p.type === "material") cargarMateriales()
		setAviso(exito(eraNuevo ? (p.status === "programada" ? "Programada" : p.status === "borrador" ? "Guardado Como Borrador" : "Publicado") : "Guardado"))
	}
	const fijar = async (p: PublicacionPortal) => { try { cambiar(await api.pin(p.id, !p.pinned)); cargarMuro() } catch (e) { setAviso(problema(mensajeDeError(e))) } }
	const abrirRedactor = async (modo: "anuncio" | "material" | "pendiente" | "encuesta", editando: PublicacionPortal | null = null) => {
		if (mat === null) cargarMateriales()
		setRedactor({ modo, editando })
		setAviso(null)
		//Editing from far down the wall: bring the editor (top of the wall) into view (QA B4).
		if (editando) window.setTimeout(() => document.querySelector(".pc .pcRedactor, .pc .pcPrincipal")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0)
	}
	const masMuro = async () => {
		try { const m = await api.wall(pagina + 1); setPosts((l) => [...(l || []), ...m.posts]); setHayMas(m.hasMore); setPagina(pagina + 1) } catch (e) { setAviso(problema(mensajeDeError(e))) }
	}

	const pc = res.nextClass
	const ahora = Date.now()
	const aulaAbierta = pc && res.hasAula && ahora >= pc.opensAtMs && ahora <= pc.closesAtMs
	const abrirAula = async () => {
		setAbriendoAula(true); setAviso(null)
		try { await abrirAulaEnPestana(async () => (await aulaService.openRoom(h)).data.url) } catch (e) { setAviso(problema(mensajeDeAula(e))) } finally { setAbriendoAula(false) }
	}

	const guardarUnidad = async (d: { name: string; startsOn: string | null; endsOn: string | null }) => {
		const lista = unidadForm?.editar ? await api.updateUnit(unidadForm.editar.id, d) : await api.createUnit(d)
		const editada = !!unidadForm?.editar
		setUnidadForm(null); setMat((m) => (m ? { ...m, units: lista } : m)); cargarMateriales(); cargarResumen(); setVersionTareas((v) => v + 1)
		setAviso(exito(editada ? "Unidad Guardada" : "Unidad Creada"))
	}
	const borrarUnidad = async (u: UnidadPortal) => {
		if (!window.confirm("¿Borrar la unidad \"" + u.name + "\"? Sus materiales se quedan sin unidad.")) return
		try { await api.deleteUnit(u.id); cargarMateriales(); cargarResumen() } catch (e) { setAviso(problema(mensajeDeError(e))) }
	}

	const tarjetaDe = (p: PublicacionPortal, compacta = false) => (
		<TarjetaPublicacion key={p.id} api={api} post={p} esAlumno={false} puedeComentar compacta={compacta}
			onCambio={cambiar} onQuitar={quitar} onEditar={(x) => abrirRedactor(x.type === "material" ? "material" : x.type === "pendiente" ? "pendiente" : x.type === "encuesta" ? "encuesta" : "anuncio", x)} onFijar={fijar}
			onAbrirTarea={(x) => (x.assignment?.delivery === "quiz" ? onResultados(x.id, "muro") : onCalificar(x.id, "muro"))} />
	)

	return (
		<div className="pc">
			<div className="pcPagina">
				<nav aria-label="Ruta" className="pcMigas">
					<button type="button" onClick={onVolver}>Mis Clases</button><span className="pcTenue" aria-hidden="true">/</span><b>{res.title}</b>
				</nav>

				<section aria-label="Portada" className="pcHero" style={fondoDePortada(a)}>
					{a.coverPhotoUrl ? <div className="pcHeroSombra" /> : null}
					<PatronSvg patron={a.coverPattern} />
					<div className="pcHeroChips">
						<span className="pcChip">{res.modality === "virtual" ? "Virtual" : "Presencial"}</span>
						{res.unit ? <span className="pcChip">Unidad {res.unit.index} De {res.unit.total}</span> : null}
						{res.role === "comaestro" ? <span className="pcChip">Co-Maestro</span> : null}
					</div>
					<div>
						<h1 className="pcHeroTitulo">{res.title}</h1>
						{res.period ? <div className="pcHeroPeriodo">{res.period}</div> : null}
					</div>
					<div className="pcHeroDatos">
						<span>{[res.scheduleLabel, res.mentor, res.students + (res.students === 1 ? " Alumno" : " Alumnos")].filter(Boolean).join(" \u00b7\u00a0")}</span>
						<button type="button" className="pcBtn pcBtnChico pcHeroBoton" onClick={onPersonalizar}><Icono n="sliders" />Personalizar</button>
					</div>
				</section>

				<nav aria-label="Secciones de la clase" className="pcTabs">
					{PESTANAS.map((t) => <button key={t.id} type="button" className={"pcTab" + (pestana === t.id ? " pcTabOn" : "")} aria-current={pestana === t.id ? "page" : undefined} onClick={() => { setPestana(t.id); setRedactor(null); setUnidadForm(null) }}>{t.texto}</button>)}
				</nav>
				<LineaAviso a={aviso} />

				{pestana === "notas" ? <LibroDeNotasPanel api={apiN} cursoTitulo={res.title} onCalificar={(id, quiz) => (quiz ? onResultados(id, "notas") : onCalificar(id, "notas"))} /> : null}

				<div className="pcColumnas" style={pestana === "notas" ? { display: "none" } : undefined}>
					<div className="pcPrincipal">
						{pestana === "muro" ? (
							<>
								{redactor && redactor.modo === "encuesta" ? (
									<RedactorEncuesta api={api} inicial={redactor.editando} onGuardado={alGuardarPost} onCancelar={() => setRedactor(null)} />
								) : redactor ? (
									<Redactor api={api} modo={redactor.modo as "anuncio" | "material" | "pendiente"} unidades={unidades} inicial={redactor.editando} onGuardado={alGuardarPost} onCancelar={() => setRedactor(null)} />
								) : (
									<div className="pcTarjeta">
										<div className="pcFilaAutor">
											<Avatar a={{ name: yo.nombre || res.mentor || "", initials: yo.iniciales || res.mentorInitials, photo: null, isTeacher: true }} />
											<button type="button" className="pcInput" style={{ textAlign: "left", color: "var(--pc-suave)", cursor: "text" }} onClick={() => abrirRedactor("anuncio")}>Escribe Un Anuncio A Tu Clase</button>
										</div>
										<div className="pcRedactorBotones">
											<button type="button" className="pcBtn pcBtnS pcBtnChico" onClick={() => abrirRedactor("anuncio")}><Icono n="megafono" />Anuncio</button>
											<button type="button" className="pcBtn pcBtnS pcBtnChico" onClick={() => abrirRedactor("material")}><Icono n="doc" />Material</button>
											<button type="button" className="pcBtn pcBtnS pcBtnChico" onClick={() => abrirRedactor("pendiente")}><Icono n="check" />Pendiente</button>
											<button type="button" className="pcBtn pcBtnS pcBtnChico" onClick={() => abrirRedactor("encuesta")}><Icono n="encuesta" />Encuesta</button>
										</div>
									</div>
								)}
								{posts === null ? <div className="pcCargando">Cargando…</div> : posts.length === 0 ? <div className="pcVacio">Aún No Hay Publicaciones</div> : posts.map((p) => tarjetaDe(p))}
								{hayMas ? <button type="button" className="pcBtn pcBtnS" onClick={masMuro}>Ver Más</button> : null}
							</>
						) : null}

						{pestana === "tareas" ? (
							<>
								{unidadForm ? <FormUnidad inicial={unidadForm.editar || undefined} onGuardar={guardarUnidad} onCancelar={() => setUnidadForm(null)} /> : null}
								<TareasPanel key={versionTareas} api={apiT} puedeCrear onNueva={onNuevaTarea} onNuevaUnidad={() => setUnidadForm({ editar: null })} onEditar={onEditarTarea} onCalificar={onCalificar} onResultados={onResultados} />
							</>
						) : null}

						{pestana === "materiales" ? (
							<>
								{redactor && redactor.modo !== "encuesta" ? <Redactor api={api} modo="material" unidades={unidades} inicial={redactor.editando} onGuardado={alGuardarPost} onCancelar={() => setRedactor(null)} /> : (
									<div className="pcRedactorBotones">
										<button type="button" className="pcBtn pcBtnP" onClick={() => abrirRedactor("material")}>Nuevo Material</button>
										<button type="button" className="pcBtn pcBtnS" onClick={() => setUnidadForm({ editar: null })}>Nueva Unidad</button>
									</div>
								)}
								{unidadForm ? <FormUnidad inicial={unidadForm.editar || undefined} onGuardar={guardarUnidad} onCancelar={() => setUnidadForm(null)} /> : null}
								{mat === null ? <div className="pcCargando">Cargando…</div> : (
									<>
										{mat.units.map((u) => {
											const ms = mat.posts.filter((p) => p.unit?.id === u.id)
											return (
												<section key={u.id} className="pcGrupo" aria-label={u.name}>
													<div className="pcUnidadTitulo">
														<h3>{u.name}</h3>
														{u.inherited ? <span className="pcChip">Del Curso</span> : <>
															<button type="button" className="pcEnlaceBtn" onClick={() => setUnidadForm({ editar: u })}>Editar</button>
															<button type="button" className="pcEnlaceBtn" onClick={() => borrarUnidad(u)}>Borrar</button>
														</>}
													</div>
													{ms.length === 0 ? <div className="pcVacio">Sin Materiales</div> : ms.map((p) => tarjetaDe(p, true))}
												</section>
											)
										})}
										{(() => {
											const sueltos = mat.posts.filter((p) => !p.unit)
											if (sueltos.length === 0 && mat.units.length > 0) return null
											return (
												<section className="pcGrupo" aria-label="Sin unidad">
													{mat.units.length > 0 ? <div className="pcUnidadTitulo"><h3>Sin Unidad</h3></div> : null}
													{sueltos.length === 0 ? <div className="pcVacio">Aún No Hay Materiales</div> : sueltos.map((p) => tarjetaDe(p, true))}
												</section>
											)
										})()}
									</>
								)}
							</>
						) : null}

						{pestana === "contenido" ? (
							<PanelContenido api={apiCont} onCambio={() => { cargarMuro(); if (mat !== null) cargarMateriales(); cargarResumen() }} />
						) : null}

						{pestana === "alumnos" ? (
							alumnos === null ? <div className="pcCargando">Cargando…</div> : alumnos.students.length === 0 ? <div className="pcVacio">Esta Clase Aún No Tiene Alumnos</div> : (
								<div className="pcAlumnos">
									{alumnos.students.map((s) => (
											<div className="pcAlumno" key={s.id}>
												<Avatar a={{ ...s, isTeacher: false }} size={36} />
												<span style={{ flex: 1, minWidth: 0 }}>
													<span style={{ display: "block" }}>{s.name}</span>
													{s.attendance && s.attendance.pct !== null ? <span className="pcTenue pcChico" style={{ fontWeight: 500 }}>Asistencia {notaTxt(s.attendance.pct)} %</span> : null}
												</span>
												{s.average !== undefined ? (
													<span title="Promedio" aria-label={"Promedio " + (s.average === null ? "sin notas aún" : notaTxt(s.average))} className={"pcChip " + (s.average === null ? "pcChipBorde" : s.low || esBaja(s.average, alumnos.minGrade) ? "pcChipOro" : "pcChipAcento")}>{s.average === null ? "–" : notaTxt(s.average)}</span>
												) : null}
											</div>
										))}
								</div>
							)
						) : null}
					</div>

					<aside className="pcLateral">
						{res.grading && res.grading.pending > 0 ? (
							<div className="pcTarjeta" style={{ gap: 6 }}>
								<div className="pcEtiqueta">Por Calificar</div>
								<div style={{ display: "flex", alignItems: "baseline", gap: 8 }}><span className="tkNumeroGrande">{res.grading.pending}</span><span className="pcTenue">{res.grading.pending === 1 ? "Entrega" : "Entregas"}</span></div>
								{res.grading.tasks.length > 1 ? res.grading.tasks.map((t) => <button key={t.id} type="button" className="tkLink" style={{ fontWeight: 600, fontSize: 14 }} onClick={() => (t.delivery === "quiz" ? onResultados(t.id, "muro") : onCalificar(t.id, "muro"))}>{t.title} · {t.pending}</button>) : null}
								<button type="button" className="tkLink" onClick={() => {
									const g = res.grading
									if (!g || g.firstTaskId === null) return
									if (g.tasks.find((x) => x.id === g.firstTaskId)?.delivery === "quiz") onResultados(g.firstTaskId, "muro"); else onCalificar(g.firstTaskId, "muro")
								}}>Empezar A Calificar</button>
							</div>
						) : null}
						{res.attention && res.attention.length > 0 ? (
							<div className="pcTarjeta">
								<div className="pcEtiqueta">Necesitan Atención</div>
								<div className="tkMuroLista">
									{res.attention.map((a) => (
										<div className="tkMuroFila" key={a.id}>
											<span className="pcAv tkAvOro" style={{ width: 30, height: 30, fontSize: 11 }} aria-hidden="true">{a.photo ? <img src={a.photo} alt="" /> : a.initials}</span>
											<span style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
												<span>{a.name}</span>
												<span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{a.reasons.map((r) => <span key={r} className="pcChip pcChipOro">{r}</span>)}</span>
											</span>
										</div>
									))}
								</div>
							</div>
						) : null}
						<div className="pcTarjeta pcProxima">
							<div className="pcEtiqueta">Próxima Clase</div>
							{pc ? (
								<>
									<div className="pcProximaFila">
										<div className="pcFecha"><span>{pc.dayShort}</span><span>{pc.dayNumber}</span></div>
										<div style={{ minWidth: 0 }}>
											<div style={{ fontWeight: 700, fontSize: 16 }}>{pc.range}</div>
											<div className="pcTenue pcChico">{pc.isToday ? "Hoy" : pc.label}</div>
										</div>
									</div>
									{res.hasAula
										? <div className="pcLugar"><Icono n="video" />Aula Virtual{!aulaAbierta && ahora < pc.opensAtMs ? " · Abre " + pc.opensLabel : ""}</div>
										: <div className="pcLugar"><Icono n="lugar" />{[res.classroom, res.campus].filter(Boolean).join(" · ")}</div>}
									{res.hasAula
										? <button type="button" className="pcBtn pcBtnP" disabled={!aulaAbierta || abriendoAula} onClick={abrirAula}>Abrir Aula</button>
										: <button type="button" className="pcBtn pcBtnP" onClick={() => navigate("/attendances")}>Pasar Asistencia</button>}
								</>
							) : <div className="pcVacio">Sin Clases Próximas</div>}
						</div>
					</aside>
				</div>
			</div>
		</div>
	)
}

// ---------------------------------------------------------------- Personalizar

const COLORES: [string, string][] = [["Naranja WAPP", "#F45030"], ["Tinta", "#2B1C16"], ["Café", "#6B3E26"], ["Ciruela", "#6A2E4F"], ["Canela", "#B4622D"], ["Arcilla", "#8C3B2A"]]
const PATRONES: [AjustesPortal["coverPattern"], string][] = [["circ", "Círculos"], ["lin", "Líneas"], ["liso", "Liso"]]
const MURO: [AjustesPortal["wallMode"], string][] = [["maestro", "Solo Yo"], ["comentan", "Alumnos Comentan"], ["publican", "Alumnos Publican"]]
const TOGGLES: [keyof Pick<AjustesPortal, "showMaterials" | "showClassmates" | "showGradeOnReturn" | "showGroupAverage">, string][] = [
	["showMaterials", "Materiales"], ["showClassmates", "Lista De Compañeros"], ["showGradeOnReturn", "Sus Notas Al Momento De Calificar"], ["showGroupAverage", "Promedio Del Grupo"]]

const Personalizar: React.FC<{ h: number; onCerrar: (guardado: boolean, mensaje?: string) => void }> = ({ h, onCerrar }) => {
	const api: PortalApiPersonal = React.useMemo(() => apiPersonal(h), [h])
	const [datos, setDatos] = useState<{ res: ResumenClase } | null>(null)
	const [aj, setAj] = useState<AjustesPortal | null>(null)
	const [foto, setFoto] = useState<{ key: string | null; vista: string | null } | null>(null)   // null = sin cambios
	const [guardando, setGuardando] = useState<boolean>(false)
	const [subiendo, setSubiendo] = useState<boolean>(false)
	const [error, setError] = useState<string | null>(null)
	const entrada = useRef<HTMLInputElement>(null)
	const apiPesos = React.useMemo(() => apiContenido("horario", h), [h])
	const [pesosCambiaron, setPesosCambiaron] = useState<boolean>(false)
	const [inicial, setInicial] = useState<string>("")

	useEffect(() => { api.overview().then((r) => { setDatos({ res: r }); setAj(r.settings); setInicial(JSON.stringify(r.settings)) }).catch((e) => setError(mensajeDeError(e))) }, [api])
	if (!datos || !aj) return <div className="pc">{error ? <div className="pcPagina"><div className="pcAviso pcOro" role="alert">{error}</div></div> : <div className="pcCargando">Cargando…</div>}</div>
	const res = datos.res
	const poner = (c: Partial<AjustesPortal>) => setAj((a) => (a ? { ...a, ...c } : a))
	const vistaFoto = foto ? foto.vista : aj.coverPhotoUrl
	const portada: AjustesPortal = { ...aj, coverPhotoUrl: vistaFoto }

	const elegirFoto = async (f: File | undefined) => {
		if (!f) return
		setError(null); setSubiendo(true)
		try {
			const reducida = await reducirImagen(f, 1600)
			const key = await subirArchivo(api, reducida, "portada")
			setFoto({ key, vista: URL.createObjectURL(reducida) })
		} catch (e) { setError(mensajeDeError(e, "No se pudo subir la foto.")) } finally { setSubiendo(false); if (entrada.current) entrada.current.value = "" }
	}
	const guardar = async () => {
		setGuardando(true); setError(null)
		try {
			await api.saveSettings({
				coverColor: aj.coverColor, coverPattern: aj.coverPattern, welcome: aj.welcome || "", wallMode: aj.wallMode,
				showMaterials: aj.showMaterials, showClassmates: aj.showClassmates, showGradeOnReturn: aj.showGradeOnReturn, showGroupAverage: aj.showGroupAverage,
				...(foto ? { coverPhotoKey: foto.key } : {})
			})
			onCerrar(true, "Cambios Guardados")
		} catch (e) { setError(mensajeDeError(e)); setGuardando(false) }
	}
	//Same as Nueva Tarea: leaving with unsaved cover/settings changes asks first (the weights save on their own).
	const cerrar = () => {
		const sucio = foto !== null || JSON.stringify(aj) !== inicial
		if (sucio && !window.confirm("¿Salir sin guardar? Se pierden los cambios de la portada y de los ajustes.")) return
		onCerrar(pesosCambiaron, pesosCambiaron ? "Pesos Guardados" : undefined)
	}

	return (
		<div className="pc">
			<header className="pcCabeza">
				<button type="button" className="pcIb" aria-label="Cerrar" onClick={cerrar}><Icono n="cerrar" size={22} /></button>
				<div className="pcCabezaTitulo"><div className="pcT">Personalizar Clase</div><div className="pcTenue pcChico">{[res.title, res.period].filter(Boolean).join(" · ")}</div></div>
				<button type="button" className="pcBtn pcBtnP" disabled={guardando || subiendo} onClick={guardar}>Guardar</button>
			</header>
			<main className="pcPersonalizar">
				<div className="pcPersonalizarCol">
					{error ? <div className="pcAviso pcOro" role="alert">{error}</div> : null}
					<section className="pcTarjeta" style={{ padding: 20 }}>
						<h2 className="pcH2">Portada</h2>
						<div className="pcColores">
							{COLORES.map(([nombre, hex]) => (
								<button key={hex} type="button" aria-label={nombre} aria-pressed={aj.coverColor === hex} className={"pcColor" + (aj.coverColor === hex ? " pcColorOn" : "")}
									style={{ background: hex, boxShadow: aj.coverColor === hex ? "0 0 0 2px " + hex : undefined }} onClick={() => poner({ coverColor: hex })} />
							))}
							<input ref={entrada} type="file" accept="image/*" hidden onChange={(e) => elegirFoto(e.target.files?.[0])} />
							<button type="button" className="pcBtn pcBtnS" style={{ minHeight: 48 }} disabled={subiendo} onClick={() => entrada.current?.click()}><Icono n="foto" />{subiendo ? "Subiendo…" : "Subir Foto"}</button>
							{vistaFoto ? <button type="button" className="pcEnlaceBtn" onClick={() => setFoto({ key: null, vista: null })}>Quitar Foto</button> : null}
						</div>
						<div className="pcOpciones">
							{PATRONES.map(([id, texto]) => <button key={id} type="button" aria-pressed={aj.coverPattern === id} className={"pcOpcion" + (aj.coverPattern === id ? " pcOpcionOn" : "")} onClick={() => poner({ coverPattern: id })}>{texto}</button>)}
						</div>
					</section>
					<section className="pcTarjeta" style={{ padding: 20 }}>
						<label htmlFor="pzBienvenida" className="pcH2">Mensaje De Bienvenida</label>
						<textarea id="pzBienvenida" className="pcTextarea" rows={3} maxLength={500} value={aj.welcome || ""} onChange={(e) => poner({ welcome: e.target.value })} />
					</section>
					<section className="pcTarjeta" style={{ padding: 20 }}>
						<h2 className="pcH2">Quién Publica En El Muro</h2>
						<div className="pcOpciones">
							{MURO.map(([id, texto]) => <button key={id} type="button" aria-pressed={aj.wallMode === id} className={"pcOpcion" + (aj.wallMode === id ? " pcOpcionOn" : "")} onClick={() => poner({ wallMode: id })}>{texto}</button>)}
						</div>
					</section>
					<section className="pcTarjeta" style={{ padding: "8px 20px", gap: 0 }}>
						<h2 className="pcH2" style={{ padding: "12px 0" }}>Lo Que Ven Los Alumnos</h2>
						{TOGGLES.map(([k, texto]) => (
							<div className="pcInterruptorFila" key={k}>
								<span>{texto}</span>
								<button type="button" role="switch" aria-checked={aj[k]} aria-label={texto} className={"pcSw" + (aj[k] ? " pcSwOn" : "")} onClick={() => poner({ [k]: !aj[k] } as Partial<AjustesPortal>)} />
							</div>
						))}
					</section>
					<section className="pcTarjeta" style={{ padding: 20 }}>
						<PanelEvaluacion api={apiPesos} compacto onCambio={() => setPesosCambiaron(true)} />
					</section>
				</div>
				<aside aria-label="Vista previa" className="pcVista">
					<div className="pcEtiqueta">Así La Ven Tus Alumnos</div>
					<div className="pcCelular"><div className="pcCelularPantalla">
						<div className="pcMiniPortada" style={fondoDePortada(portada)}>
							{portada.coverPhotoUrl ? <div className="pcHeroSombra" /> : null}
							<PatronSvg patron={aj.coverPattern} chico />
							<div className="pcT" style={{ position: "relative", fontSize: 22, fontWeight: 800, lineHeight: 1.05, overflowWrap: "anywhere" }}>{res.title}</div>
							{res.period ? <div style={{ position: "relative", fontSize: 13, fontWeight: 700 }}>{res.period}</div> : null}
							<div style={{ position: "relative", fontSize: 12.5, fontWeight: 600 }}>{[res.scheduleLabel, res.mentor].filter(Boolean).join(" · ")}</div>
						</div>
						{aj.welcome ? <div className="pcMiniCard">{aj.welcome}</div> : null}
					</div></div>
				</aside>
			</main>
		</div>
	)
}

// ---------------------------------------------------------------- Nueva Tarea y Calificar (Parte B)

/** Compact headers: course, then period. */
const tituloConPeriodo = (r: { title: string; period?: string | null }): string => [r.title, r.period].filter(Boolean).join(" · ")

const PantallaNuevaTarea: React.FC<{ h: number; t: number | null; onCerrar: (mensaje: string | null) => void }> = ({ h, t, onCerrar }) => {
	const api: PortalApiPersonal = React.useMemo(() => apiPersonal(h), [h])
	const apiT = React.useMemo(() => apiTareasPersonal(h), [h])
	const [res, setRes] = useState<ResumenClase | null>(null)
	const [error, setError] = useState<string | null>(null)
	useEffect(() => { api.overview().then(setRes).catch((e) => setError(mensajeDeError(e, "No pudimos abrir esta clase."))) }, [api])
	if (error) return <div className="pc"><div className="pcPagina"><button type="button" className="pcEnlaceBtn" onClick={() => onCerrar(null)}>Volver</button><div className="pcAviso pcOro" role="alert">{error}</div></div></div>
	if (!res) return <div className="pc"><div className="pcCargando">Cargando…</div></div>
	return <NuevaTarea api={api} apiT={apiT} res={res} postId={t} onCerrar={onCerrar} />
}

const PantallaCalificar: React.FC<{ h: number; t: number; onCerrar: () => void }> = ({ h, t, onCerrar }) => {
	const api: PortalApiPersonal = React.useMemo(() => apiPersonal(h), [h])
	const apiT = React.useMemo(() => apiTareasPersonal(h), [h])
	const [titulo, setTitulo] = useState<string>("")
	useEffect(() => { api.overview().then((r) => setTitulo(tituloConPeriodo(r))).catch(() => setTitulo("")) }, [api])
	return <Calificar apiT={apiT} postId={t} cursoTitulo={titulo} onCerrar={onCerrar} onCambio={() => undefined} />
}

const PantallaResultados: React.FC<{ h: number; t: number; onCerrar: () => void; onEditar: () => void }> = ({ h, t, onCerrar, onEditar }) => {
	const api: PortalApiPersonal = React.useMemo(() => apiPersonal(h), [h])
	const apiT = React.useMemo(() => apiTareasPersonal(h), [h])
	const apiQ = React.useMemo(() => apiQuizPersonal(h), [h])
	const [titulo, setTitulo] = useState<string>("")
	useEffect(() => { api.overview().then((r) => setTitulo(tituloConPeriodo(r))).catch(() => setTitulo("")) }, [api])
	return <ResultadosQuizPantalla apiQ={apiQ} apiT={apiT} postId={t} cursoTitulo={titulo} onCerrar={onCerrar} onEditar={onEditar} />
}

// ---------------------------------------------------------------- ruta

const ClassPortalController: React.FC = () => {
	const [params, setParams] = useSearchParams()
	const [version, setVersion] = useState<number>(0)
	const [aviso, setAviso] = useState<string | null>(null)
	const h = Number(params.get("h")) || null
	const vista = params.get("v")
	const t = Number(params.get("t")) || null

	if (h === null) return <ListaDeClases onAbrir={(id) => setParams({ h: String(id) })} />
	if (vista === "personalizar") return <Personalizar h={h} onCerrar={(g, mensaje) => { if (g) setVersion(version + 1); setAviso(mensaje || null); setParams({ h: String(h) }) }} />
	if (vista === "nuevaTarea") return <PantallaNuevaTarea h={h} t={t} onCerrar={(m) => { setAviso(m); setParams({ h: String(h), p: "tareas" }) }} />
	if (vista === "calificar" && t !== null) return <PantallaCalificar h={h} t={t} onCerrar={() => setParams({ h: String(h), p: params.get("de") === "notas" ? "notas" : params.get("de") === "muro" ? "muro" : "tareas" })} />
	if (vista === "resultados" && t !== null) {
		const de = params.get("de")
		return <PantallaResultados h={h} t={t} onCerrar={() => setParams({ h: String(h), p: de === "notas" ? "notas" : de === "muro" ? "muro" : "tareas" })} onEditar={() => { setAviso(null); setParams({ h: String(h), v: "nuevaTarea", t: String(t) }) }} />
	}
	return <PortalDeClase key={h} h={h} refrescarAjustes={version} onVolver={() => setParams({})} onPersonalizar={() => setParams({ h: String(h), v: "personalizar" })}
		pestanaInicial={(["muro", "tareas", "materiales", "contenido", "alumnos", "notas"].includes(params.get("p") || "") ? params.get("p") : undefined) as Pestana | undefined} avisoInicial={aviso}
		onNuevaTarea={() => { setAviso(null); setParams({ h: String(h), v: "nuevaTarea" }) }} onEditarTarea={(id) => { setAviso(null); setParams({ h: String(h), v: "nuevaTarea", t: String(id) }) }} onCalificar={(id, desde) => { setAviso(null); setParams({ h: String(h), v: "calificar", t: String(id), ...(desde ? { de: desde } : {}) }) }}
		onResultados={(id, desde) => { setAviso(null); setParams({ h: String(h), v: "resultados", t: String(id), ...(desde ? { de: desde } : {}) }) }} />
}

export default ClassPortalController
